// Feature: features/nma-appeal-templates-demo-ui.md, features/payer-policy-postgres-sync-coding-and-appeals-integration.md
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Select,
  Input,
  Textarea,
  Button,
  Checkbox,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  HStack,
  VStack,
  Divider,
  Alert,
  AlertIcon,
  Collapse,
  Spinner,
  Link,
  useToast,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  AttachmentIcon,
  AddIcon,
  RepeatIcon,
  EditIcon,
  ExternalLinkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@chakra-ui/icons';
import { callApi } from '../../callApi';
import { AppContext } from '../AppContext';
import {
  PROCEDURE_TYPE_OPTIONS,
  SUBMISSION_CHANNEL_OPTIONS,
  matchTemplatesForClaim,
  hydrateTemplateText,
  buildAppealJobCustomInstructions,
} from './appealTemplatesStore';
import { formatCurrency } from './shared';

const NMAAppealLaunchModal = ({
  isOpen,
  onClose,
  claimRow,
  templates = [],
  savedSelection = null,
  isSubmitting = false,
  forceRegenerate = false,
  accessToken: propAccessToken = null,
  onConfirmLaunch,
  onSwitchToTemplatesTab,
}) => {
  const appCtx = useContext(AppContext);
  const accessToken = propAccessToken || appCtx?.accessToken;
  const toast = useToast();
  const extraFileInputRef = useRef(null);

  const [selectedProcedure, setSelectedProcedure] = useState('');
  const [submissionChannel, setSubmissionChannel] = useState('STANDARD_APPEAL_PACKET');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [addTemplateDropdownId, setAddTemplateDropdownId] = useState('');
  const [compiledText, setCompiledText] = useState('');
  const [isTextManuallyEdited, setIsTextManuallyEdited] = useState(false);
  const [billerNotes, setBillerNotes] = useState('');
  const [extraFiles, setExtraFiles] = useState([]);

  // Payer Policy Authority & Interactive Policy Selection State
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [resolvedAuthority, setResolvedAuthority] = useState(null);
  const [recommendedPolicies, setRecommendedPolicies] = useState([]);
  const [catalogPolicies, setCatalogPolicies] = useState([]);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState([]);
  const [expandedPolicyIds, setExpandedPolicyIds] = useState([]);
  const [addPolicyDropdownId, setAddPolicyDropdownId] = useState('');

  // Run the intelligent matcher whenever claimRow, templates, or selectedProcedure changes
  const matchResult = useMemo(() => {
    if (!claimRow) {
      return {
        inferredProcedure: {
          procedureType: PROCEDURE_TYPE_OPTIONS[2],
          shortLabel: 'Spine IONM',
          evidenceCodes: [],
          reason: '',
        },
        effectiveProcedure: PROCEDURE_TYPE_OPTIONS[2],
        recommendedChannel: 'STANDARD_APPEAL_PACKET',
        matchedTemplates: [],
        otherTemplates: [],
      };
    }
    return matchTemplatesForClaim(claimRow, templates, selectedProcedure);
  }, [claimRow, templates, selectedProcedure]);

  // Initialize modal state when opened for a claim
  useEffect(() => {
    if (!isOpen || !claimRow) return;
    const initialMatch = matchTemplatesForClaim(claimRow, templates, '');
    const initProc = savedSelection?.procedure_type || initialMatch.inferredProcedure.procedureType;
    const initChannel = savedSelection?.submission_channel || initialMatch.recommendedChannel;

    const procMatch = matchTemplatesForClaim(claimRow, templates, initProc);
    const defaultIds =
      savedSelection?.template_ids && savedSelection.template_ids.length > 0
        ? savedSelection.template_ids.filter((id) => templates.some((t) => t.id === id))
        : procMatch.matchedTemplates.map((t) => t.id);

    setSelectedProcedure(initProc);
    setSubmissionChannel(initChannel);
    setSelectedTemplateIds(
      defaultIds.length > 0 ? defaultIds : procMatch.matchedTemplates.map((t) => t.id)
    );
    setIsTextManuallyEdited(false);
    setBillerNotes(savedSelection?.biller_notes || '');
    setExtraFiles(savedSelection?.extra_files || []);
    setAddTemplateDropdownId('');
    setExpandedPolicyIds([]);
    setAddPolicyDropdownId('');
  }, [isOpen, claimRow, templates, savedSelection]);

  // Fetch canonical authority & recommended payer policies when the modal opens
  useEffect(() => {
    if (!isOpen || !claimRow) return undefined;

    const applyPolicyBundle = (bundle) => {
      const auth = bundle?.resolved_authority || null;
      const rec = Array.isArray(bundle?.recommended_policies) ? bundle.recommended_policies : [];
      const cat = Array.isArray(bundle?.catalog_policies) ? bundle.catalog_policies : [];
      setResolvedAuthority(auth);
      setRecommendedPolicies(rec);
      setCatalogPolicies(cat);

      if (
        Array.isArray(savedSelection?.selected_policy_ids) &&
        savedSelection.selected_policy_ids.length > 0
      ) {
        setSelectedPolicyIds(savedSelection.selected_policy_ids.map((id) => Number(id)));
      } else {
        const autoIds = rec.filter((p) => p.auto_select).map((p) => Number(p.doc_id));
        setSelectedPolicyIds(autoIds.length > 0 ? autoIds : rec.slice(0, 1).map((p) => Number(p.doc_id)));
      }
    };

    if (claimRow.policy_recommendations?.resolved_authority) {
      applyPolicyBundle(claimRow.policy_recommendations);
      return undefined;
    }

    if (!accessToken) return undefined;
    let isCancelled = false;
    setLoadingPolicies(true);
    const qs = new URLSearchParams({
      claim_number: claimRow.claim_number || '',
      payer: claimRow.payer_name || '',
      carrier: claimRow.carrier_name || claimRow.payer_name || '',
      state: claimRow.facility_state || '',
      dos: claimRow.date_of_service || '',
    }).toString();

    callApi(`/api/nma/appeals/policies?${qs}`, accessToken)
      .then((res) => res.json())
      .then((data) => {
        if (!isCancelled && data?.status === 'success') {
          applyPolicyBundle(data);
        }
      })
      .catch((err) => {
        console.error('Error loading appeal policies:', err);
      })
      .finally(() => {
        if (!isCancelled) setLoadingPolicies(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [isOpen, claimRow, accessToken, savedSelection]);

  // All known policy objects indexed by doc_id
  const allPoliciesById = useMemo(() => {
    const map = {};
    recommendedPolicies.forEach((p) => {
      map[Number(p.doc_id)] = p;
    });
    catalogPolicies.forEach((p) => {
      if (!map[Number(p.doc_id)]) {
        map[Number(p.doc_id)] = p;
      }
    });
    return map;
  }, [recommendedPolicies, catalogPolicies]);

  // Policies visible in the interactive checklist (all recommended + any manually added catalog policies)
  const displayedPolicyCards = useMemo(() => {
    const recIds = new Set(recommendedPolicies.map((p) => Number(p.doc_id)));
    const addedFromCatalog = selectedPolicyIds
      .filter((id) => !recIds.has(Number(id)))
      .map((id) => allPoliciesById[Number(id)])
      .filter(Boolean);
    return [...recommendedPolicies, ...addedFromCatalog];
  }, [recommendedPolicies, selectedPolicyIds, allPoliciesById]);

  const activeSelectedPolicies = useMemo(
    () => selectedPolicyIds.map((id) => allPoliciesById[Number(id)]).filter(Boolean),
    [selectedPolicyIds, allPoliciesById]
  );

  // Resolved template objects currently selected
  const activeSelectedTemplates = useMemo(() => {
    const reasonMap = {};
    (matchResult.matchedTemplates || []).forEach((m) => {
      reasonMap[m.id] = m.matchReasons || [];
    });
    return selectedTemplateIds
      .map((id) => {
        const found = templates.find((t) => t.id === id);
        if (!found) return null;
        return {
          ...found,
          matchReasons: reasonMap[id] || ['Manually Selected Override'],
          isAutoMatched: Boolean(reasonMap[id]),
        };
      })
      .filter(Boolean);
  }, [selectedTemplateIds, templates, matchResult.matchedTemplates]);

  // Auto-hydrate and merge template text unless the user manually edited the textarea
  useEffect(() => {
    if (!claimRow || isTextManuallyEdited) return;
    const blocks = activeSelectedTemplates.map((tpl) => {
      const hydrated = hydrateTemplateText(
        tpl.template_text,
        claimRow,
        selectedProcedure,
        activeSelectedPolicies
      );
      return `=== [TEMPLATE: ${tpl.name}] ===\n${hydrated}`;
    });
    setCompiledText(blocks.join('\n\n'));
  }, [
    activeSelectedTemplates,
    activeSelectedPolicies,
    claimRow,
    selectedProcedure,
    isTextManuallyEdited,
  ]);

  const handleTogglePolicy = (docId) => {
    const numId = Number(docId);
    setSelectedPolicyIds((prev) =>
      prev.includes(numId) ? prev.filter((id) => id !== numId) : [...prev, numId]
    );
    setIsTextManuallyEdited(false);
  };

  const handleTogglePolicyExpand = (docId) => {
    const numId = Number(docId);
    setExpandedPolicyIds((prev) =>
      prev.includes(numId) ? prev.filter((id) => id !== numId) : [...prev, numId]
    );
  };

  const handleAddPolicyFromDropdown = (docIdStr) => {
    if (!docIdStr) return;
    const numId = Number(docIdStr);
    if (!selectedPolicyIds.includes(numId)) {
      setSelectedPolicyIds((prev) => [...prev, numId]);
      setIsTextManuallyEdited(false);
    }
    setAddPolicyDropdownId('');
  };

  const handleProcedureChange = (newProc) => {
    setSelectedProcedure(newProc);
    const nextMatch = matchTemplatesForClaim(claimRow, templates, newProc);
    setSelectedTemplateIds(nextMatch.matchedTemplates.map((t) => t.id));
    setIsTextManuallyEdited(false);
  };

  const handleToggleTemplate = (tplId) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(tplId) ? prev.filter((id) => id !== tplId) : [...prev, tplId]
    );
    setIsTextManuallyEdited(false);
  };

  const handleAddTemplateFromDropdown = (tplId) => {
    if (!tplId) return;
    if (!selectedTemplateIds.includes(tplId)) {
      setSelectedTemplateIds((prev) => [tplId, ...prev]);
      setIsTextManuallyEdited(false);
    }
    setAddTemplateDropdownId('');
  };

  const handleExtraFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const added = files.map((file) => ({
      id: `extra-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: file.name,
      size_kb: Math.max(1, Math.round(file.size / 1024)),
      role: 'Case Supplement',
    }));
    setExtraFiles((prev) => [...prev, ...added]);
    if (extraFileInputRef.current) {
      extraFileInputRef.current.value = '';
    }
    toast({
      title: 'Supplemental File Attached',
      description: `Attached ${files.map((f) => f.name).join(', ')} to this appeal.`,
      status: 'info',
      duration: 2500,
      isClosable: true,
    });
  };

  const handleConfirm = () => {
    if (!claimRow) return;
    const customInstructions = buildAppealJobCustomInstructions({
      selectedTemplates: activeSelectedTemplates,
      selectedPolicies: activeSelectedPolicies,
      resolvedAuthority,
      procedureType: selectedProcedure,
      submissionChannel,
      compiledTemplateText: compiledText,
      extraFiles,
      billerNotes,
    });

    const selectionMeta = {
      claim_number: claimRow.claim_number,
      procedure_type: selectedProcedure,
      submission_channel: submissionChannel,
      template_ids: selectedTemplateIds,
      template_names: activeSelectedTemplates.map((t) => t.name),
      selected_policy_ids: selectedPolicyIds,
      selected_policy_labels: activeSelectedPolicies.map(
        (p) => `[${p.policy_reference_number || `DOC-${p.doc_id}`}] ${p.title}`
      ),
      resolved_authority_name: resolvedAuthority?.display_name || null,
      attached_files_count:
        activeSelectedTemplates.reduce((acc, t) => acc + (t.attached_files?.length || 0), 0) +
        extraFiles.length,
      extra_files: extraFiles,
      biller_notes: billerNotes,
    };

    onConfirmLaunch({
      claimRow,
      customInstructions,
      selectedPolicyIds,
      selectionMeta,
      force: forceRegenerate,
    });
  };

  if (!claimRow) return null;

  const availableToAdd = (templates || []).filter((t) => !selectedTemplateIds.includes(t.id));
  const deniedCpts =
    claimRow.denied_core_cpts && claimRow.denied_core_cpts.length > 0
      ? claimRow.denied_core_cpts
      : claimRow.denied_cpts || [];
  const billedIcds =
    claimRow.billed_icd10_codes && claimRow.billed_icd10_codes.length > 0
      ? claimRow.billed_icd10_codes
      : claimRow.revedy_icd10_codes || [];

  const totalReferenceFiles = [
    ...activeSelectedTemplates.flatMap((t) =>
      (t.attached_files || []).map((f) => ({ ...f, sourceTemplate: t.name }))
    ),
    ...extraFiles.map((f) => ({ ...f, sourceTemplate: 'One-Off Claim Attachment' })),
  ];

  const activeChannelObj =
    SUBMISSION_CHANNEL_OPTIONS.find((c) => c.value === submissionChannel) ||
    SUBMISSION_CHANNEL_OPTIONS[0];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="92vh">
        <ModalHeader bg="gray.800" color="white" borderTopRadius="md" py={3.5}>
          <Flex justify="space-between" align="center" pr={8} wrap="wrap" gap={2}>
            <Box>
              <HStack spacing={2.5} wrap="wrap">
                <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5}>
                  PRE-FLIGHT TEMPLATE REVIEW
                </Badge>
                <Text fontSize="md" fontWeight="bold">
                  {forceRegenerate ? 'Regenerate Appeal / eAppeal:' : 'Create Appeal / Kick Off eAppeal:'}{' '}
                  {claimRow.claim_number}
                </Text>
                <Badge colorScheme="blue" fontSize="xs">
                  {claimRow.payer_name}
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.300" fontWeight="normal" mt={1}>
                Review the auto-matched payer &amp; procedure templates below, swap or customize if needed, and launch the appeal job.
              </Text>
            </Box>

            {onSwitchToTemplatesTab && (
              <Button
                size="xs"
                variant="outline"
                colorScheme="teal"
                color="teal.200"
                borderColor="teal.400"
                _hover={{ bg: 'gray.700' }}
                leftIcon={<EditIcon />}
                onClick={() => {
                  onClose();
                  onSwitchToTemplatesTab();
                }}
              >
                Open Template Library
              </Button>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody py={4} bg="gray.50">
          <VStack spacing={4} align="stretch">
            {/* 1. Claim Context, Procedure Detection & Submission Channel */}
            <Card variant="outline" bg="white" borderTop="3px solid #3182CE">
              <CardBody py={3} px={4}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                  <Box>
                    <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                      Claim / Patient / DOS
                    </Text>
                    <Text fontSize="xs" fontWeight="bold" color="gray.800">
                      {claimRow.claim_number} (Case #{claimRow.nma_case_no}) • {claimRow.patient_name || 'N/A'}
                    </Text>
                    <Text fontSize="11px" color="gray.600">
                      DOS: {claimRow.date_of_service || 'N/A'} • POS {claimRow.place_of_service || 'N/A'} •{' '}
                      {claimRow.facility_name || 'Facility'}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                      Disputed CPTs &amp; Remittance
                    </Text>
                    <HStack spacing={1} wrap="wrap" mt={0.5}>
                      {deniedCpts.map((cpt) => (
                        <Badge key={cpt} colorScheme="red" fontSize="10px">
                          {cpt}
                        </Badge>
                      ))}
                      {claimRow.top_carc && (
                        <Badge colorScheme="orange" fontSize="10px">
                          {claimRow.top_carc}
                          {claimRow.top_rarc ? ` / ${claimRow.top_rarc}` : ''}
                        </Badge>
                      )}
                      <Text fontSize="xs" fontWeight="bold" color="red.600" ml={1}>
                        ({formatCurrency(claimRow.core_denied_amount)})
                      </Text>
                    </HStack>
                    <Text fontSize="11px" color="gray.600" noOfLines={1} mt={0.5} title={claimRow.primary_denial_reason}>
                      {claimRow.primary_denial_reason || 'Denied per payer remittance'}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                      Billed ICD-10 Diagnoses
                    </Text>
                    <HStack spacing={1} wrap="wrap" mt={0.5}>
                      {billedIcds.length > 0 ? (
                        billedIcds.slice(0, 6).map((dx, i) => (
                          <Badge key={dx} colorScheme={i === 0 ? 'blue' : 'gray'} fontSize="10px">
                            {String.fromCharCode(65 + i)}: {dx}
                          </Badge>
                        ))
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          See Case Record
                        </Text>
                      )}
                    </HStack>
                    <Text fontSize="10px" color="green.700" mt={1}>
                      {matchResult.inferredProcedure.reason}
                    </Text>
                  </Box>
                </SimpleGrid>

                <Divider mb={3} />

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.700">
                        Detected Procedure Type (Change to Re-Match Templates)
                      </Text>
                      <Badge colorScheme="green" variant="subtle" fontSize="10px">
                        Auto-Detected: {matchResult.inferredProcedure.shortLabel}
                      </Badge>
                    </HStack>
                    <Select
                      size="sm"
                      bg="blue.50"
                      borderColor="blue.300"
                      fontWeight="semibold"
                      value={selectedProcedure}
                      onChange={(e) => handleProcedureChange(e.target.value)}
                    >
                      {PROCEDURE_TYPE_OPTIONS.map((proc) => (
                        <option key={proc} value={proc}>
                          {proc}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" fontWeight="bold" color="gray.700">
                        Appeal / eAppeal Submission Mode
                      </Text>
                      <Badge colorScheme={activeChannelObj.color} fontSize="10px">
                        {activeChannelObj.badge}
                      </Badge>
                    </HStack>
                    <Select
                      size="sm"
                      value={submissionChannel}
                      onChange={(e) => setSubmissionChannel(e.target.value)}
                    >
                      {SUBMISSION_CHANNEL_OPTIONS.map((ch) => (
                        <option key={ch.value} value={ch.value}>
                          {ch.label}
                        </option>
                      ))}
                    </Select>
                  </Box>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* 2. Canonical Payer Policy Authority & Interactive Policy Selector */}
            <Card variant="outline" bg="white" borderTop="3px solid #805AD5">
              <CardHeader py={2.5} px={4} borderBottom="1px solid" borderColor="gray.100">
                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                  <HStack spacing={2} wrap="wrap">
                    <Heading size="xs" color="gray.800" textTransform="uppercase">
                      2. Select Payer Policies &amp; CMS LCDs to Cite ({activeSelectedPolicies.length})
                    </Heading>
                    {loadingPolicies ? (
                      <HStack spacing={1.5}>
                        <Spinner size="xs" color="purple.500" />
                        <Text fontSize="10px" color="gray.500">
                          Resolving Canonical Authority...
                        </Text>
                      </HStack>
                    ) : resolvedAuthority ? (
                      <>
                        <Badge colorScheme="purple" fontSize="10px">
                          {resolvedAuthority.display_name}
                        </Badge>
                        <Badge colorScheme="blue" variant="subtle" fontSize="10px">
                          {resolvedAuthority.lob_cohort}
                        </Badge>
                        {resolvedAuthority.is_out_of_state_host_claim ? (
                          <Badge colorScheme="orange" variant="solid" fontSize="10px">
                            BlueCard Home-Plan State: {resolvedAuthority.effective_plan_state} (Service State: {resolvedAuthority.facility_state})
                          </Badge>
                        ) : (
                          resolvedAuthority.effective_plan_state &&
                          resolvedAuthority.effective_plan_state !== 'ALL' && (
                            <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                              Plan State: {resolvedAuthority.effective_plan_state}
                            </Badge>
                          )
                        )}
                      </>
                    ) : null}
                  </HStack>

                  {/* + Add Another Payer Policy from Library Dropdown */}
                  <HStack spacing={2}>
                    <Select
                      size="xs"
                      w="320px"
                      bg="white"
                      placeholder="+ Add Another Payer Policy from Library..."
                      value={addPolicyDropdownId}
                      onChange={(e) => handleAddPolicyFromDropdown(e.target.value)}
                    >
                      {[...recommendedPolicies, ...catalogPolicies]
                        .filter((pol) => !selectedPolicyIds.includes(Number(pol.doc_id)))
                        .map((pol) => (
                          <option key={pol.doc_id} value={pol.doc_id}>
                            [{pol.policy_reference_number || `DOC-${pol.doc_id}`}] {pol.title.slice(0, 55)} ({pol.authority_display_name || pol.domain})
                          </option>
                        ))}
                    </Select>
                  </HStack>
                </Flex>
                {resolvedAuthority?.geo_resolution_note && (
                  <Text fontSize="11px" color="gray.600" mt={1}>
                    {resolvedAuthority.geo_resolution_note}
                  </Text>
                )}
              </CardHeader>

              <CardBody py={3} px={4}>
                {displayedPolicyCards.length === 0 ? (
                  <Alert status="info" borderRadius="md" fontSize="xs">
                    <AlertIcon />
                    No auto-matched payer policy documents found for this carrier. You can select any policy from the library dropdown above.
                  </Alert>
                ) : (
                  <VStack align="stretch" spacing={2.5}>
                    {displayedPolicyCards.map((pol) => {
                      const docId = Number(pol.doc_id);
                      const isChecked = selectedPolicyIds.includes(docId);
                      const isExpanded = expandedPolicyIds.includes(docId);
                      const isCitedInDenial = String(pol.match_reason || '')
                        .toUpperCase()
                        .includes('CITED IN DENIAL');
                      const catColor =
                        pol.doc_category === 'PAYMENT_POLICY'
                          ? 'blue'
                          : pol.doc_category === 'CMS_LCD_ARTICLE'
                          ? 'teal'
                          : 'purple';

                      return (
                        <Box
                          key={docId}
                          p={3}
                          bg={isChecked ? 'purple.50' : 'gray.50'}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={isChecked ? 'purple.200' : 'gray.200'}
                        >
                          <Flex justify="space-between" align="flex-start" wrap="wrap" gap={2}>
                            <HStack align="flex-start" spacing={2.5} flex="1">
                              <Checkbox
                                colorScheme="purple"
                                mt={0.5}
                                isChecked={isChecked}
                                onChange={() => handleTogglePolicy(docId)}
                              />
                              <Box flex="1">
                                <HStack spacing={2} wrap="wrap" mb={0.5}>
                                  <Badge colorScheme={catColor} variant="solid" fontSize="10px">
                                    {pol.policy_reference_number || `DOC-${docId}`}
                                  </Badge>
                                  <Text fontSize="xs" fontWeight="bold" color="gray.900">
                                    {pol.title}
                                  </Text>
                                  <Badge colorScheme={catColor} variant="outline" bg="white" fontSize="9px">
                                    {pol.doc_category || 'MEDICAL_POLICY'}
                                  </Badge>
                                  {pol.published_effective_date && (
                                    <Badge colorScheme="gray" variant="subtle" fontSize="9px">
                                      Eff: {pol.published_effective_date}
                                      {pol.effective_to_date ? ` -> ${pol.effective_to_date}` : ''}
                                    </Badge>
                                  )}
                                  {pol.match_reason && (
                                    <Badge
                                      colorScheme={isCitedInDenial ? 'red' : 'green'}
                                      variant="subtle"
                                      fontSize="9px"
                                    >
                                      {isCitedInDenial ? '🎯 ' : '🏛️ '}
                                      {pol.match_reason}
                                    </Badge>
                                  )}
                                </HStack>

                                {pol.icd10_summary && (
                                  <Text fontSize="11px" color="gray.700" mt={0.5}>
                                    {pol.icd10_summary}
                                  </Text>
                                )}

                                {pol.dos_cutover_note && (
                                  <Text
                                    fontSize="10px"
                                    fontWeight="semibold"
                                    color={pol.is_effective_for_dos ? 'green.700' : 'orange.700'}
                                    mt={0.5}
                                  >
                                    {pol.dos_cutover_note}
                                  </Text>
                                )}

                                <Collapse in={isExpanded} animateOpacity>
                                  <Box
                                    mt={2}
                                    p={2.5}
                                    bg="white"
                                    borderRadius="md"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                  >
                                    <HStack spacing={3} wrap="wrap" mb={1.5} fontSize="10px">
                                      {Array.isArray(pol.covered_cpts) && pol.covered_cpts.length > 0 && (
                                        <Text color="green.700">
                                          <b>Covered CPTs:</b> {pol.covered_cpts.join(', ')}
                                        </Text>
                                      )}
                                      {Array.isArray(pol.non_covered_cpts) && pol.non_covered_cpts.length > 0 && (
                                        <Text color="red.600">
                                          <b>Non-Covered CPTs:</b> {pol.non_covered_cpts.join(', ')}
                                        </Text>
                                      )}
                                      {pol.url && pol.url.startsWith('http') && (
                                        <Link
                                          href={pol.url}
                                          isExternal
                                          color="blue.600"
                                          fontWeight="semibold"
                                        >
                                          Open Source Policy URL <ExternalLinkIcon mx="2px" />
                                        </Link>
                                      )}
                                    </HStack>
                                    <Box
                                      maxH="180px"
                                      overflowY="auto"
                                      fontFamily="mono"
                                      fontSize="10px"
                                      whiteSpace="pre-wrap"
                                      color="gray.700"
                                      bg="gray.50"
                                      p={2}
                                      borderRadius="sm"
                                    >
                                      {pol.excerpt_markdown || 'No extracted Markdown preview available.'}
                                    </Box>
                                  </Box>
                                </Collapse>
                              </Box>
                            </HStack>

                            <HStack spacing={1}>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="purple"
                                rightIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                onClick={() => handleTogglePolicyExpand(docId)}
                              >
                                {isExpanded ? 'Hide Criteria' : 'Preview Criteria'}
                              </Button>
                            </HStack>
                          </Flex>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </CardBody>
            </Card>

            {/* 3. Auto-Matched Templates & Override Selector */}
            <Card variant="outline" bg="white">
              <CardHeader py={2.5} px={4} borderBottom="1px solid" borderColor="gray.100">
                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                  <HStack spacing={2}>
                    <Heading size="xs" color="gray.800" textTransform="uppercase">
                      3. Templates Selected for This Appeal ({activeSelectedTemplates.length})
                    </Heading>
                    <Badge colorScheme="green" fontSize="10px">
                      Auto-Matched by Payer + Procedure + CPT
                    </Badge>
                  </HStack>

                  {/* Add / Swap Another Template Dropdown */}
                  <HStack spacing={2}>
                    <Select
                      size="xs"
                      w="290px"
                      bg="white"
                      placeholder="+ Add / Swap Another Template from Library..."
                      value={addTemplateDropdownId}
                      onChange={(e) => handleAddTemplateFromDropdown(e.target.value)}
                    >
                      {availableToAdd.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.payer_match} • {t.procedure_type.split(' (')[0]})
                        </option>
                      ))}
                    </Select>
                  </HStack>
                </Flex>
              </CardHeader>

              <CardBody py={3} px={4}>
                {activeSelectedTemplates.length === 0 ? (
                  <Alert status="warning" borderRadius="md" fontSize="xs">
                    <AlertIcon />
                    No templates are currently checked. Select a template from the dropdown above or type custom instructions below.
                  </Alert>
                ) : (
                  <VStack align="stretch" spacing={2.5}>
                    {activeSelectedTemplates.map((tpl) => (
                      <Box
                        key={tpl.id}
                        p={3}
                        bg={tpl.is_base_template ? 'teal.50' : 'blue.50'}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={tpl.is_base_template ? 'teal.200' : 'blue.200'}
                      >
                        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={2}>
                          <HStack align="flex-start" spacing={2.5} flex="1">
                            <Checkbox
                              colorScheme="blue"
                              mt={0.5}
                              isChecked={selectedTemplateIds.includes(tpl.id)}
                              onChange={() => handleToggleTemplate(tpl.id)}
                            />
                            <Box flex="1">
                              <HStack spacing={2} wrap="wrap" mb={0.5}>
                                <Text fontSize="xs" fontWeight="bold" color="gray.900">
                                  {tpl.name}
                                </Text>
                                {(tpl.matchReasons || []).map((reason) => (
                                  <Badge
                                    key={reason}
                                    colorScheme={tpl.isAutoMatched ? 'green' : 'purple'}
                                    variant="solid"
                                    fontSize="9px"
                                  >
                                    ✓ {reason}
                                  </Badge>
                                ))}
                              </HStack>
                              <Text fontSize="11px" color="gray.700" mb={1.5}>
                                {tpl.description}
                              </Text>

                              {(tpl.attached_files || []).length > 0 && (
                                <HStack spacing={2} wrap="wrap">
                                  <Text fontSize="10px" fontWeight="bold" color="gray.600">
                                    Included Files:
                                  </Text>
                                  {tpl.attached_files.map((f) => (
                                    <Badge
                                      key={f.id || f.name}
                                      colorScheme="blue"
                                      variant="outline"
                                      bg="white"
                                      fontSize="10px"
                                      px={1.5}
                                    >
                                      <AttachmentIcon mr={1} boxSize={2.5} />
                                      {f.name} ({f.role})
                                    </Badge>
                                  ))}
                                </HStack>
                              )}
                            </Box>
                          </HStack>

                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleToggleTemplate(tpl.id)}
                          >
                            Remove
                          </Button>
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                )}

                {/* Supplemental Reference Files & Case Packet Strip */}
                <Flex
                  mt={3}
                  pt={2.5}
                  borderTop="1px solid"
                  borderColor="gray.100"
                  justify="space-between"
                  align="center"
                  wrap="wrap"
                  gap={2}
                >
                  <HStack spacing={1.5} wrap="wrap">
                    <Text fontSize="11px" fontWeight="bold" color="gray.600">
                      Auto-Bundled Case Clinical Docs:
                    </Text>
                    <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                      ✓ Operative Report
                    </Badge>
                    <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                      ✓ Signed IONM Pro Summary
                    </Badge>
                    <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                      ✓ IONM Tech Log / Timestamps
                    </Badge>
                    <Badge colorScheme="teal" variant="subtle" fontSize="10px">
                      ✓ Hospital Face Sheet
                    </Badge>
                    {extraFiles.map((ef) => (
                      <Badge key={ef.id} colorScheme="purple" variant="solid" fontSize="10px">
                        📎 {ef.name}
                      </Badge>
                    ))}
                  </HStack>

                  <Box>
                    <input
                      ref={extraFileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.md"
                      style={{ display: 'none' }}
                      onChange={handleExtraFileUpload}
                    />
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="purple"
                      leftIcon={<AttachmentIcon />}
                      onClick={() => extraFileInputRef.current && extraFileInputRef.current.click()}
                    >
                      + Attach One-Off File for This Claim
                    </Button>
                  </Box>
                </Flex>
              </CardBody>
            </Card>

            {/* 4. Hydrated Template Preview & Inline Override Editor */}
            <Card variant="outline" bg="white">
              <CardHeader py={2.5} px={4} borderBottom="1px solid" borderColor="gray.100">
                <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                  <Box>
                    <Heading size="xs" color="gray.800" textTransform="uppercase">
                      4. Hydrated Template Instructions &amp; Boilerplate Preview (Editable)
                    </Heading>
                    <Text fontSize="11px" color="gray.500">
                      Placeholders have been populated with {claimRow.claim_number}&apos;s data. You can edit the text directly before launching.
                    </Text>
                  </Box>
                  {isTextManuallyEdited && (
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="gray"
                      leftIcon={<RepeatIcon />}
                      onClick={() => setIsTextManuallyEdited(false)}
                    >
                      Reset to Template Default
                    </Button>
                  )}
                </Flex>
              </CardHeader>

              <CardBody py={3} px={4}>
                <VStack spacing={3} align="stretch">
                  <Textarea
                    rows={10}
                    fontSize="xs"
                    fontFamily="mono"
                    bg="gray.900"
                    color="gray.100"
                    p={3}
                    borderRadius="md"
                    value={compiledText}
                    onChange={(e) => {
                      setCompiledText(e.target.value);
                      setIsTextManuallyEdited(true);
                    }}
                  />

                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={1}>
                      Additional Claim-Specific Biller Notes (Optional)
                    </Text>
                    <Input
                      size="sm"
                      placeholder="e.g., Emphasize Dr. Smith's intraoperative alert at 10:42 AM or reference prior auth #..."
                      value={billerNotes}
                      onChange={(e) => setBillerNotes(e.target.value)}
                    />
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" borderTop="1px solid" borderColor="gray.200">
          <Flex justify="space-between" align="center" w="100%" wrap="wrap" gap={2}>
            <Text fontSize="xs" color="gray.600">
              <b>{activeSelectedPolicies.length}</b> payer polic{activeSelectedPolicies.length === 1 ? 'y' : 'ies'},{' '}
              <b>{activeSelectedTemplates.length}</b> template(s) &amp;{' '}
              <b>{totalReferenceFiles.length}</b> reference file(s) will be applied to{' '}
              <b>{claimRow.claim_number}</b>.
            </Text>
            <HStack spacing={2.5}>
              <Button size="sm" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme={submissionChannel === 'PAYER_PORTAL_EAPPEAL' ? 'purple' : 'blue'}
                leftIcon={forceRegenerate ? <RepeatIcon /> : <CheckCircleIcon />}
                isLoading={isSubmitting}
                onClick={handleConfirm}
              >
                {submissionChannel === 'PAYER_PORTAL_EAPPEAL'
                  ? 'Confirm Templates & Kick Off eAppeal'
                  : forceRegenerate
                  ? 'Confirm Templates & Regenerate Appeal'
                  : 'Confirm Templates & Create Appeal Job'}
              </Button>
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NMAAppealLaunchModal;
